import { ReactNode, createContext, useContext } from 'react'

import router, { useRouter } from 'next/router'

import {
  getUrlWithCombinedQueryParam,
  getUrlWithReplacedPathParam,
  isNullish,
  commonLinks,
  webRoutes,
} from '@local/utils'

import { isFirstPage } from '@web/hooks/useGlobalFirstPage'
import { useGlobalStaticStateContext } from '@web/hooks/useGlobalStaticState'
import useNMapScheme from '@web/hooks/useNMapScheme'
import { BASE_PATH, messages, voidCallback } from '@web/libs/constants'
import links from '@web/libs/constants/links'
import routes from '@web/libs/constants/routes'
import { debugLanding } from '@web/libs/dev/MyplaceDevtools/debugLandingMode'
import { goToNaverAppSDK, shouldOpenSaveMapSDK } from '@web/libs/entry.helper'
import { nApp, nMap } from '@web/libs/scheme'
import { clearMobileCacheStorage, extractErrorMessage, neloLogger } from '@web/libs/utils'
import { PathParam, QueryParam } from '@web/types'

type Option<Path extends string> = {
  url: Path
  params: Record<PathParam<Path>, string>
  query?: Partial<Record<QueryParam<Path>, string>>
  extraQuery?: Record<string, string>
  openWindow?: boolean
  replace?: boolean
}

type OptionWithoutParams<URL extends string> = Omit<Option<URL>, 'params'>
type OptionWithParams<URL extends string> = Option<URL>
interface GoLoginOption {
  url?: string
  showAlert?: boolean
  replace?: boolean
  openWindow?: boolean
}

type GoToLogoutOption = Pick<Option<string>, 'openWindow' | 'replace'> & { redirectUrl?: string }

export interface NavigationContext {
  back: (option?: { onWeb?: () => void }) => void
  close: () => void
  push: <URL extends string>(
    option: PathParam<URL> extends never ? OptionWithoutParams<URL> : OptionWithParams<URL>,
  ) => void
  goToMy: () => void
  goToLogin: (option?: GoLoginOption) => void
  goToLogout: (option?: GoToLogoutOption) => void
  goToReviewNew: (
    visitId: string,
    option?: { replace?: boolean; myplaceSDKHeader?: string; openWindow?: boolean },
  ) => void
  goToPlaceDetail: (placeId: string) => void
}

const NavigationContext = createContext<NavigationContext>({
  back: voidCallback,
  close: voidCallback,
  push: voidCallback,
  goToMy: voidCallback,
  goToLogin: voidCallback,
  goToLogout: voidCallback,
  goToReviewNew: voidCallback,
  goToPlaceDetail: voidCallback,
})

export const NavigationContextProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter()

  const { getNMapURLScheme } = useNMapScheme()

  const {
    isFromMapsApp,
    isFromNaverApp,
    hasMapsAppSDK,
    hasNaverAppSDK,
    isFromOpenMain,
    isAndroid,
    isFromMapsAppBottomSheet,
    mapAppVersion,
  } = useGlobalStaticStateContext()

  const isFromTimelineList = router.pathname === routes.TIMELINE_LIST

  const push = <URL extends string>(
    option: PathParam<URL> extends never ? OptionWithoutParams<URL> : OptionWithParams<URL>,
  ) => {
    let url = generateUrl(option)

    let openWindow = option?.openWindow

    // openWindow가 사용처에서 manual하게 설정되어 있지 않고 외부 URL 이동인경우 새창 열기
    if (isNullish(openWindow) && !isInternalUrl(url)) {
      openWindow = true
    }

    if (isFromMapsAppBottomSheet) {
      openWindow = true
    }

    if (openWindow && isFromMapsApp && isAndroid && isInternalUrl(url)) {
      // 안드로이드 지도앱일 경우 - 새로운 윈도우로 열고 싶을 땐 `target=new` 파라미터를 붙여주어야함.
      // 프로필 클릭시에 `target=new` 붙이면 링크 오류가 나서 마플 도메인인 경우에만 `target=new` 파라미터를 붙여주는 스펙으로 변경
      // https://oss.navercorp.com/Maps/android_navermap_v5/issues/14475
      url = getUrlWithCombinedQueryParam(url, { target: 'new' })
      openNewWindow(url)
      return
    }

    if (openWindow || (url.includes('nmap') && isFromMapsApp) || isFromOpenMain) {
      openNewWindow(url)
      return
    }

    if (option?.replace) {
      replacePage(url)
      return
    }

    movePage(url)
  }

  const back: NavigationContext['back'] = ({ onWeb } = {}) => {
    if (isFirstPage()) {
      close()
      return
    }

    if (onWeb) {
      onWeb()
      return
    }

    router.back()
  }

  const close: NavigationContext['close'] = () => {
    // https://oss.navercorp.com/place/local/issues/2327
    if (isFromMapsApp) {
      window.location.href = nMap.getScheme({ type: nMap.Type.BACK, inApp: true })
      return
    }

    if (isFromNaverApp) {
      window.location.href = nApp.getScheme({ type: nApp.Type.CLOSE_WINDOW })
      return
    }

    window?.open('about:blank', '_self')?.close()
    return
  }

  const goToMy = () => {
    if (isFromMapsApp) {
      return (window.location.href = commonLinks.NMAP_MY_APP_SCHEME)
    }

    return push({ url: webRoutes.HOME })
  }

  const goToLogin = ({ url, showAlert, replace, openWindow = false }: GoLoginOption = {}) => {
    if (showAlert) alert(messages.LOGIN_NEED_ALERT)
    const currentUrl = window.location.href

    let redirectUrl = url ?? currentUrl
    if (isCSRUrl(redirectUrl)) redirectUrl = `${links.MY_HOME}${redirectUrl}`

    return push({ url: links.LOGIN, query: { url: redirectUrl }, replace, openWindow })
  }

  const goToLogout = (option: GoToLogoutOption = {}) => {
    clearMobileCacheStorage()

    return push({
      url: links.LOGOUT,
      openWindow: option.openWindow ?? false,
      replace: option.replace,
      query: { url: option.redirectUrl ?? window.location.href },
    })
  }

  const goToReviewNew = (
    visitId: string,
    {
      replace = false,
      myplaceSDKHeader,
      openWindow = false,
    }: { replace?: boolean; myplaceSDKHeader?: string; openWindow?: boolean } = {},
  ) => {
    const placeContext = myplaceSDKHeader
    if (hasMapsAppSDK) {
      return (window.location.href = getNMapURLScheme({
        path: 'myplace',
        query: { type: 'review', visitId: visitId, placeContext },
      }))
    }

    if (hasNaverAppSDK) {
      return goToNaverAppSDK({ type: 'review', visitId, placeContext })
    }

    return push({
      url: routes.REVIEW_NEW,
      params: { visitId },
      replace,
      openWindow: isFromTimelineList ?? openWindow,
    })
  }

  const goToPlaceDetail = (placeId: string) => {
    if (shouldOpenSaveMapSDK(navigator.userAgent)) return openNewWindow(`nmap://place?id=${placeId}&mode=detail`)
    return push({ url: links.PLACE_DETAIL, params: { placeId }, openWindow: true })
  }

  return (
    <NavigationContext.Provider
      value={{ back, push, close, goToMy, goToLogin, goToLogout, goToReviewNew, goToPlaceDetail }}
    >
      {children}
    </NavigationContext.Provider>
  )
}

export const openNewWindow = (url: string) => {
  if (isCSRUrl(url)) {
    url = `${window.location.origin}${BASE_PATH}${url}`
  }

  if (debugLanding({ url, type: 'openWindow' })) return

  window.open(url, '_blank')
}

export const replacePage = (url: string) => {
  if (debugLanding({ url, type: 'replace' })) return

  if (isCSRUrl(url)) {
    router.replace(url)
    return
  }

  location.replace(url)
}

export const movePage = (url: string) => {
  if (debugLanding({ url, type: 'push' })) return

  if (isCSRUrl(url)) {
    router.push(url)
    return
  }

  location.assign(url)
}

export const isRelativePath = (path: string) => {
  return path.startsWith('/')
}

export const isCSRUrl = (url: string) => {
  return isRelativePath(url) && !url.startsWith(BASE_PATH)
}

export const isInternalUrl = (url: string) => {
  try {
    if (isCSRUrl(url)) return true

    if (isRelativePath(url)) return true

    const hostPathName = new URL(url).host + new URL(url).pathname

    const myplacePattern = /place\.naver\.com\/my/

    return myplacePattern.test(hostPathName)
  } catch (error) {
    neloLogger('warn', extractErrorMessage(error))
    return false
  }
}

// myplace 도메인인 경우 상대 경로로 변환
export const convertToRelativeUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url)

    if (isInternalUrl(url)) {
      let path = parsedUrl.pathname.replace(new RegExp(`^${BASE_PATH}`), '')

      if (!path.startsWith('/')) {
        path = '/' + path
      }

      return path + (parsedUrl.search || '') + (parsedUrl.hash || '')
    }
    return url
  } catch (error) {
    return url
  }
}

export const generateUrl: <URL extends string>(
  option: PathParam<URL> extends never ? OptionWithoutParams<URL> : OptionWithParams<URL>,
) => string = option => {
  let url = option.url as string

  if (option && 'params' in option) {
    url = getUrlWithReplacedPathParam(url, option.params)
  }

  url = getUrlWithCombinedQueryParam(url, { ...option?.query, ...option?.extraQuery })

  return url
}

export const useNavigation = () => useContext(NavigationContext)
